(() => {
  'use strict';
  const TEST = 'MC_CART_ROUTINEUPGRADE_SMOOCHE';
  const PRODUCT = 9221577441436;
  const SOURCES = [8462448984220, 8758851895452, 9163492425884];
  const PLANS = [3050799260, 3023372444, 3022880924];
  const selector = `[maison-test-id="${TEST}"]`;
  const bundle = item => {
    try { return JSON.parse(item.properties?.__kaching_bundles) || {}; } catch { return {}; }
  };
  const sameBundle = (a, b) => a.id && a.v && a.id === b.id && a.v === b.v;
  function eligible(cart, key) {
    // A cart-wide discount can change when a line is replaced; do not quote a misleading difference.
    if (cart.cart_level_discount_applications?.length) return null;
    const source = cart.items.find(item => item.key === key);
    if (!source || !SOURCES.includes(source.product_id) || source.quantity !== 1 || source.final_price <= 0) return null;
    if (cart.items.some(item => item.product_id === PRODUCT)) return null;
    const bottleCount = cart.items.reduce((total, item) => total + (
      SOURCES.includes(item.product_id) && item.final_price > 0 && !bundle(item).gift ? item.quantity : 0
    ), 0);
    if (bottleCount !== 1) return null;
    const data = bundle(source);
    if (data.gift || data.upsell) return null;
    const plan = source.selling_plan_allocation?.selling_plan;
    if (plan && !PLANS.includes(plan.id)) return null;
    // Never break a multi-product or multi-unit bundle to offer a single routine.
    if (cart.items.some(item => item.key !== key && sameBundle(bundle(item), data) && !bundle(item).gift)) return null;
    return source;
  }
  function removalUpdates(cart, source) {
    const updates = { [source.key]: 0 };
    const data = bundle(source);
    for (const item of cart.items) {
      if (item.final_price === 0 && bundle(item).gift && sameBundle(bundle(item), data)) updates[item.key] = 0;
    }
    return updates;
  }
  if (typeof module === 'object' && module.exports) {
    module.exports = { eligible, removalUpdates, bundle };
    return;
  }
  if (window.smoocheRoutineUpgradeReady) return;
  window.smoocheRoutineUpgradeReady = true;
  const root = window.Shopify?.routes?.root || '/';
  const states = new WeakMap();
  let offerPromise, refreshing = false, queued = false, busy = false, successKey = '';
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency, maximumFractionDigits: amount % 100 ? 2 : 0,
  }).format(amount / 100);
  async function request(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin', cache: 'no-store',
      ...(body ? { method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error('The cart could not be updated. Please try again.');
    const result = await response.json();
    if (result.errors || result.status >= 400) throw new Error('The routine is unavailable. Your original item has been kept.');
    return result;
  }
  async function loadOffer() {
    // Read the native PDP configuration so versioned discounts and gifts stay in sync.
    const response = await fetch(root + 'products/complete-reverse-aging-system-for-mature-women?view=srm-cff-ingredients', {credentials: 'same-origin', cache: 'no-store'});
    if (!response.ok) throw new Error('Routine offer unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const read = query => JSON.parse(doc.querySelector(query).textContent);
    const config = read('#kaching-bundles-config');
    const product = read(`.kaching-bundles-product[data-product-id="${PRODUCT}"]`);
    const deal = read(`.kaching-bundles-deal-block-settings[data-product-id="${PRODUCT}"]`);
    const bar = deal.dealBars.find(item => item.id === deal.preselectedDealBarId);
    const variant = product.variants.find(item => item.id === product.selectedVariantId && item.availableForSale);
    const plan = product.sellingPlans.find(item => item.id === 3100442780);
    if (!variant || !bar || bar.quantity !== 1 || bar.discountType !== 'specific' || deal.nanoId !== 'QuKT' || !plan) throw new Error('Routine offer changed');
    const allocation = variant.sellingPlans.find(item => item.id === plan.id);
    if (!allocation || plan.priceAdjustment.type !== 'fixed_amount') throw new Error('Routine plan changed');
    const oneTime = Math.round(bar.discountValue * 100 * Number(config.currencyRate || 1));
    const price = deal.priceRounding ? Math.round(oneTime / 100) * 100 : oneTime;
    const subscription = price - (variant.price - allocation.price);
    const gifts = bar.freeGifts.filter(item => item.productGID).map(item => {
      const id = item.productGID.split('/').pop();
      const giftProduct = read(`.kaching-bundles-product[data-product-id="${id}"]`);
      const giftVariant = giftProduct.variants.find(v => v.id === giftProduct.selectedVariantId && v.availableForSale);
      if (!giftVariant || item.quantity !== 1 || item.applyOnlyForSubscriptions) throw new Error('Routine gifts changed');
      return { id: giftVariant.id, gift: item.id, value: giftVariant.compareAtPrice || giftVariant.price };
    });
    if (gifts.length !== 3 || !gifts.some(g => g.gift === 'p_uD') || !gifts.some(g => g.gift === 'D4oC') || !gifts.some(g => g.gift === 'zDx9')) throw new Error('Routine gifts changed');
    return { product, variant, plan, deal, bar, gifts, price, subscription, currency: config.marketCurrencyCode };
  }
  const getOffer = () => offerPromise || (offerPromise = loadOffer().catch(error => { offerPromise = null; throw error; }));
  function payload(offer, subscription, id) {
    const metadata = {deal: offer.deal.nanoId, pp: PRODUCT, main: true, v: offer.deal.versionId, id, bid: offer.bar.id};
    if (subscription) metadata.sellingPlan = {id: offer.plan.id, priceAdjustment: offer.plan.priceAdjustment};
    return {items: [
      {id: offer.variant.id, quantity: 1, properties: {__kaching_bundles: JSON.stringify(metadata)}, ...(subscription ? {selling_plan: offer.plan.id} : {})},
      ...offer.gifts.map(gift => ({id: gift.id, quantity: 1, properties: {__kaching_bundles: JSON.stringify({id, deal: offer.deal.nanoId, v: offer.deal.versionId, gift: gift.gift})}})),
    ].reverse()};
  }
  function verifyRoutine(cart, offer, id, subscription, expected) {
    const lines = cart.items.filter(item => bundle(item).id === id);
    const main = lines.find(item => item.product_id === PRODUCT);
    return main && main.quantity === 1 && main.final_line_price === expected &&
      (main.selling_plan_allocation?.selling_plan.id || null) === (subscription ? offer.plan.id : null) &&
      offer.gifts.every(g => lines.some(item => item.variant_id === g.id && item.quantity === 1 && item.final_line_price === 0));
  }
  function analytics(name, detail) {
    document.dispatchEvent(new CustomEvent('maison:routine-upgrade:' + name, {detail: {testId: TEST, ...detail}}));
  }
  function showSuccess() {
    if (!successKey) return;
    const line = Array.from(document.querySelectorAll('#cart-drawer [data-cart-item]')).find(item => item.dataset.item === successKey);
    if (!line || line.querySelector('[data-routine-success]')) return;
    const message = document.createElement('p');
    message.className = 'routine-upgrade__success';
    message.dataset.routineSuccess = '';
    message.setAttribute('role', 'status');
    const check = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    check.setAttribute('viewBox', '0 0 24 24');
    check.setAttribute('aria-hidden', 'true');
    check.setAttribute('focusable', 'false');
    check.innerHTML = '<path d="m5 12 4 4L19 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>';
    message.append(check, document.createTextNode('Congratulations! Your routine deal and free gifts are in your cart.'));
    line.append(message);
  }
  async function refresh() {
    showSuccess();
    const drawer = document.querySelector('#cart-drawer');
    const footer = drawer?.querySelector('[maison-test-id="MC_CART_GUARANTEEBANNER_SMOOCHE"]');
    drawer?.classList.toggle('guarantee-footer-active', !!footer && getComputedStyle(footer).display !== 'none');
    const syncUpsells = () => drawer?.classList.toggle('routine-upgrade-active', Array.from(
      drawer.querySelectorAll('[data-routine-upgrade]')
    ).some(card => getComputedStyle(card).display !== 'none' && !card.querySelector('.routine-upgrade__card').hidden));
    syncUpsells();
    if (refreshing || busy) return;
    const cards = Array.from(document.querySelectorAll('#cart-drawer [data-routine-upgrade]' + selector))
      .filter(card => getComputedStyle(card).display !== 'none' && !states.has(card));
    if (!cards.length) return;
    refreshing = true;
    try {
      const [offer, cart] = await Promise.all([getOffer(), request('cart.js')]);
      if (offer.currency !== cart.currency) return;
      for (const card of cards) {
        if (!card.isConnected) continue;
        const source = eligible(cart, card.dataset.sourceKey);
        if (!source) continue;
        const subscription = !!source.selling_plan_allocation;
        const price = subscription ? offer.subscription : offer.price;
        const extra = price - source.final_line_price;
        if (extra <= 0) continue;
        states.set(card, {source, offer, subscription, price, extra});
        card.querySelector('[data-routine-price]').textContent = subscription
          ? `${money(price, cart.currency)} total today`
          : `${money(price, cart.currency)} total for both products`;
        for (const [name, id] of [['brush', 'p_uD'], ['blender', 'D4oC'], ['gift', 'zDx9']]) {
          card.querySelector(`[data-routine-value="${name}"]`).textContent = `(${money(offer.gifts.find(g => g.gift === id).value, cart.currency)} value)`;
        }
        card.querySelector('[data-routine-gift-total]').textContent = money(offer.gifts.reduce((total, gift) => total + gift.value, 0), cart.currency);
        card.querySelector('[data-routine-extra]').textContent = money(extra, cart.currency);
        const compare = card.querySelector('[data-routine-compare]');
        const regularPrice = Number(card.dataset.routineRegularPrice);
        compare.textContent = money(regularPrice, cart.currency);
        compare.hidden = !(regularPrice > extra);
        card.querySelector('[data-routine-accept]').setAttribute('aria-label', `Upgrade my routine for ${money(extra, cart.currency)} extra`);
        card.querySelector('.routine-upgrade__card').hidden = false;
        analytics('exposure', {productId: source.product_id, purchaseType: subscription ? 'subscription' : 'one-time'});
      }
    } catch (error) {
      // An unavailable or changed native offer must never become a misleading card.
      console.warn('Routine upgrade unavailable:', error.message);
    } finally { refreshing = false; syncUpsells(); }
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; refresh(); });
  }
  async function accept(card) {
    const state = states.get(card);
    if (busy || !state || getComputedStyle(card).display === 'none') return;
    busy = true;
    const button = card.querySelector('[data-routine-accept]');
    const labelNode = button.querySelector('[data-routine-label]');
    const label = labelNode.textContent;
    button.disabled = true;
    labelNode.textContent = 'Upgrading your routine…';
    card.setAttribute('aria-busy', 'true');
    const errorNode = card.querySelector('[data-routine-error]');
    errorNode.hidden = true;
    const id = 'MC' + crypto.randomUUID().replaceAll('-', '').slice(0, 12);
    let source, attempted = false;
    try {
      const before = await request('cart.js');
      source = eligible(before, state.source.key);
      if (!source || source.final_line_price !== state.source.final_line_price || before.currency !== state.offer.currency) throw new Error('Your cart changed. Please reopen the cart to see your updated offer.');
      attempted = true;
      // Kaching's own add requests use this flag to skip its pre-purchase popup.
      // Scope it to this complete routine, which already includes the free brush.
      await request('cart/add.js?kaching_popup=true', payload(state.offer, state.subscription, id));
      const added = await request('cart.js');
      if (!verifyRoutine(added, state.offer, id, state.subscription, state.price)) throw new Error('This deal could not be applied. Your original item has been kept.');
      if (added.total_price !== before.total_price + state.price) throw new Error('Your discounts changed. Your original item has been kept.');
      const original = added.items.find(item => item.key === source.key);
      if (!original || original.quantity !== 1) throw new Error('Your cart changed during the upgrade. Please try again.');
      const updates = removalUpdates(added, source);
      // One request removes the selected product and only its associated free gifts.
      await request('cart/update.js', {updates});
      const final = await request('cart.js');
      if (final.items.some(item => item.key === source.key) || !verifyRoutine(final, state.offer, id, state.subscription, state.price)) throw new Error('Please check your cart before continuing.');
      successKey = final.items.find(item => item.product_id === PRODUCT && bundle(item).id === id).key;
      analytics('accepted', {productId: source.product_id, extra: state.extra, currency: final.currency});
    } catch (error) {
      // Resolve uncertain network responses before deciding whether to undo the new bundle.
      let completed = false;
      if (attempted) {
        try {
          const cart = await request('cart.js');
          if (!cart.items.some(item => item.key === source.key) && verifyRoutine(cart, state.offer, id, state.subscription, state.price)) {
            completed = true;
            successKey = cart.items.find(item => item.product_id === PRODUCT && bundle(item).id === id).key;
          } else if (cart.items.some(item => item.key === source.key)) {
            const updates = Object.fromEntries(cart.items.filter(item => bundle(item).id === id).map(item => [item.key, 0]));
            if (Object.keys(updates).length) await request('cart/update.js', {updates});
          }
        } catch { error = new Error('We could not confirm the upgrade. Please check your cart before trying again.'); }
      }
      if (!completed) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
        analytics('failed', {productId: state.source.product_id});
      }
    } finally {
      busy = false;
      button.disabled = false;
      labelNode.textContent = label;
      card.removeAttribute('aria-busy');
      if (successKey) {
        document.querySelectorAll('#cart-drawer ' + selector + ' .routine-upgrade__card').forEach(node => { node.hidden = true; });
        if (window.cart?.getCart) window.cart.getCart();
      }
    }
  }
  // Block additional drawer mutations while the swap is being verified.
  window.addEventListener('click', event => {
    if (busy && event.target.closest?.('#cart-drawer button, #cart-drawer a, #cart-drawer input')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  window.addEventListener('submit', event => {
    if (busy && event.target.closest?.('#cart-drawer')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-routine-accept]');
    if (button?.closest('#cart-drawer')) accept(button.closest(selector));
  });
  ['theme:cart:loaded', 'theme:cart:added', 'theme:cart:open'].forEach(name => document.addEventListener(name, schedule));
  function initialize() {
    const drawer = document.querySelector('#cart-drawer');
    if (!drawer) return;
    new MutationObserver(schedule).observe(drawer, {childList: true, subtree: true});
    // Elevate may inject its stylesheet after the drawer script initializes.
    new MutationObserver(schedule).observe(document.head, {childList: true, subtree: true});
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
})();
